import React, { useMemo } from "react";
import { Pie } from "react-chartjs-2";
import { Box, Card, CircularProgress, Typography } from "@material-ui/core";
import { connect } from "react-redux";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";
import { INTERNAL_RESEARCH_STATUS_LABEL } from "../../../constants/internalResearchStatus";

// Refactor 2026-05-21: dùng `INTERNAL_RESEARCH_STATUS_LABEL` (30 status mới)
// thay vì enum legacy ("info_default", "report_default", ...) — sau refactor
// 2026-05-15 BE đã chuyển sang state machine action-based, status cũ không
// còn tồn tại trong DB → count luôn = 0 → biểu đồ rỗng.
//
// Pie chart chỉ render status có count > 0. Color rotate khi quá enum màu.
const PIE_COLORS = [
  "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40",
  "#C9CBCF", "#8E5EA2", "#3CBA9F", "#E8C3B9", "#C45850", "#2ECC71",
  "#3498DB", "#9B59B6", "#F39C12", "#1ABC9C", "#D35400", "#7F8C8D",
  "#16A085", "#27AE60", "#2980B9", "#8E44AD", "#F1C40F", "#E67E22",
  "#34495E", "#95A5A6", "#E74C3C", "#BDC3C7", "#46A6A0", "#A23B72",
];

// extractStatus — chuẩn hoá status: BE trả string hoặc table.rowRender wrap
// thành array `[{value, label, bgcolor}]` — luôn lấy về 1 string value cuối.
const extractStatus = (raw) => {
  if (!raw) return "";
  if (typeof raw === "string") {
    const parts = raw.split(",");
    return parts[parts.length - 1];
  }
  if (Array.isArray(raw) && raw.length > 0) {
    const last = raw[raw.length - 1];
    return typeof last === "object" ? last?.value : last;
  }
  return "";
};

function InstitutionalProjectStatistics(props) {
  const { internalResearchList, isFetching } = props;

  // Đếm số đề tài theo status hiện tại — chỉ count status nằm trong enum mới;
  // status legacy / lạ gom vào "(Khác)".
  const statusCount = useMemo(() => {
    const counts = {};
    let unknown = 0;
    (internalResearchList || []).forEach((ir) => {
      const s = extractStatus(ir?.status);
      if (!s) return;
      if (INTERNAL_RESEARCH_STATUS_LABEL[s] !== undefined) {
        counts[s] = (counts[s] || 0) + 1;
      } else {
        unknown += 1;
      }
    });
    if (unknown > 0) counts.__unknown__ = unknown;
    return counts;
  }, [internalResearchList]);

  const total = useMemo(
    () => Object.values(statusCount).reduce((s, v) => s + v, 0),
    [statusCount]
  );

  // Build chart data — chỉ giữ entry có count > 0. Sort giảm dần để slice
  // lớn ở đầu legend dễ đọc.
  const chartData = useMemo(() => {
    const entries = Object.entries(statusCount)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    return {
      labels: entries.map(([k]) =>
        k === "__unknown__" ? "(Khác / legacy)" : INTERNAL_RESEARCH_STATUS_LABEL[k] || k
      ),
      datasets: [
        {
          data: entries.map(([, v]) => v),
          backgroundColor: entries.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]),
        },
      ],
    };
  }, [statusCount]);

  const pieOptions = {
    title: { display: false },
    legend: {
      position: "bottom",
      labels: { fontSize: 11, boxWidth: 15 },
    },
    maintainAspectRatio: false,
  };

  return (
    <Card style={{ height: "100%", padding: "12px 16px", display: "flex", flexDirection: "column" }}>
      {isFetching ? (
        <Box display="flex" justifyContent="center" alignItems="center" style={{ height: "100%" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box style={{ marginBottom: 8 }}>
            <Typography variant="h6" style={{ fontSize: 14, fontWeight: "bold", textAlign: "center" }}>
              Thống kê đề tài theo trạng thái
            </Typography>
          </Box>
          <Box style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
            {total > 0 ? (
              <div style={{ width: "100%", height: "100%" }}>
                <Pie data={chartData} options={pieOptions} />
              </div>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Không có dữ liệu để hiển thị
              </Typography>
            )}
          </Box>
          <Box style={{ textAlign: "center", marginTop: 8 }}>
            <Typography variant="body2" style={{ fontSize: 13 }}>
              <strong>Tổng số đề tài: {total}</strong>
            </Typography>
          </Box>
        </>
      )}
    </Card>
  );
}

const mapStateToProps = (state) => ({
  internalResearchList: select(state, "internalResearchReducer", "list"),
  isFetching: select(state, "internalResearchReducer", "isFetching"),
});

export default connect(mapStateToProps, null)(toJs(InstitutionalProjectStatistics));
